/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import cx from 'classnames';

export default class StickyTableHeaderHelper extends React.PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        scrollingContainerId: PropTypes.string.isRequired,
        isFullView: PropTypes.bool,
    };

    static defaultProps = {
        isFullView: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            isStuck: false,
            scrollPosition: 0,
            bottom: 0,
        };

        this.container = undefined;
        this.header = undefined;
        this.hasAlerts = false;
        this.headerAlertsBottom = 0;
        this.throttledSetSticky = throttle(this.setSticky, 100);
        this.isDesktop = window.screen.width > 767;
        this.timeout = null;
    }

    componentDidMount() {
        this.tableWrapper = document.querySelector('.per-availability-table-container');
        this.container = document.getElementById(this.props.scrollingContainerId);
        this.header = document.querySelector('header.nav-header-container');
        window.addEventListener('resize', this.throttledSetSticky);
        document.addEventListener('scroll', this.setSticky);

        if (this.container && this.isDesktop) {
            this.container.addEventListener('scroll', this.throttledSetSticky);
        }

        this.tableWrapper.addEventListener('scroll', this.handleWrapperScroll);
        this.setSticky();
    }

    componentDidUpdate() {
        // If the user clicks the "Expand Table" button, the action bar may resize,
        // so we're waiting an arbitrary amount of time for the page the re-render
        // and then calling setSticky() to recalculate headerAlertsBottom

        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.setSticky();
        }, 300);
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);

        window.removeEventListener('resize', this.throttledSetSticky);
        document.removeEventListener('scroll', this.setSticky);

        if (this.container && this.isDesktop) {
            this.container.removeEventListener('scroll', this.setSticky);
        }

        this.tableWrapper.removeEventListener('scroll', this.handleWrapperScroll);
    }

    setSticky = () => {
        // We only care about the alert position if the device can display the left-right
        // container split
        if (this.isDesktop) {
            const headerBottom = this.header
                ? this.header.getBoundingClientRect().bottom
                : 0;
            let headerAlertsBottom = 0;

            const headerAlerts = document.querySelector('.nav-header-alerts');
            if (headerAlerts) {
                headerAlertsBottom = headerAlerts.getBoundingClientRect().bottom;
            }

            const bounding = this.tableWrapper.getBoundingClientRect();

            const bottom =
                headerAlertsBottom > headerBottom ? headerAlertsBottom : headerBottom;
            const isStuck = bounding.top < bottom && bounding.bottom > bottom;

            this.setState({
                isStuck,
                bottom,
            });
        } else {
            const bottom = this.header ? 70 : 0;
            const bounding = this.tableWrapper.getBoundingClientRect();
            const isStuck = bounding.top < bottom && bounding.bottom > bottom;

            this.setState({
                isStuck,
                bottom,
            });
        }
    };

    setInnerStickyRef = (node) => {
        this.innerStickyRef = node;
    };

    handleWrapperScroll = () => {
        const scrollPosition = this.tableWrapper.scrollLeft;
        this.setState({ scrollPosition });
    };

    render() {
        const { scrollPosition, isStuck, bottom } = this.state;
        const { isFullView } = this.props;

        const style = cx('sticky-table-header-helper', {
            sticky: isStuck,
            expand: isFullView,
        });

        return (
            <div className={style} style={{ top: bottom }}>
                <div className="sticky-table-header-helper-wrapper">
                    <div style={{ transform: `translate3d(-${scrollPosition}px, 0, 0)` }}>
                        {this.props.children}
                    </div>
                </div>
            </div>
        );
    }
}
